import React, { useState, useEffect, useRef } from 'react';
import { 
  Lightbulb,        // Hint
  SkipBack,         // Revert
  Undo,             // Undo
  Eraser,           // Erase
  LogOut,           // Abandon
  Check,            // Submit/Validate
  Eye,              // Reveal
  Settings,         // Operation
  Trash2,           // Delete
  Palette,          // Color
  MousePointer      // Select
} from 'lucide-react';
import { useDevice } from '../../context/DeviceContext';
import { usePuzzleState } from '../../context/PuzzleStateContext';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import styles from './ActionPanelNarrow.module.css';
import { useMetadata } from '../../context/MetadataContext';
import { useUser } from '../../context/UserContext';
import ConfirmationDialog from '../ConfirmationDialog'; 

const ActionPanelNarrow = ({ 
  actions = [],
  allowedOperations = [],
  allowedColors = [],
  loading,
  selectedCell
}) => {
  const { getText, getThemePalettes } = useMetadata();
  const { userData } = useUser();

  // State for color picker
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef(null);
  
  // State for abandon confirmation dialog
  const [showAbandonConfirmation, setShowAbandonConfirmation] = useState(false);
  
  // Get puzzle state functions from context
  const {
    getHint,
    undoMove,
    eraseCell,
    setSelectedCell,
    validatePuzzle,
    revealCell,
    operationCell,
    deleteCell,
    colorCell,
    selectCell,
    createRevert
  } = usePuzzleState();
  
  // Get game state functions from context
  const {
    abandonPuzzle,
    submitSolution
  } = useGameState();
  
  // Get global error trigger function from context
  const { triggerError } = useGlobalError();

  // Get active mode to determine filtering
  const { activeMode } = useGameState();
  
  // Get all actions and combine them in a single horizontal row
  const abandonAction = actions.find(a => a.action === 'action_abandon');
  const submitAction = activeMode === 'create' 
    ? actions.find(a => a.action === 'action_submit')
    : actions.find(a => a.action === 'action_submit' || a.action === 'action_validate');
  
  // Filter out action_operation and replace with individual operation buttons
  // Also filter out action_note since notes are now handled by the value selector
  const actionsToFilter = ['action_abandon', 'action_submit', 'action_validate', 'action_operation', 'action_note'];
  if (activeMode === 'create') {
    actionsToFilter.push('action_revert');
  }
  const baseActions = actions.filter(a => !actionsToFilter.includes(a.action));
  
  // Create individual operation actions based on allowed_operations
  const operationActions = allowedOperations.map(op => ({
    action: `operation_${op.operator}`,
    enabled: true,
    operator: op.operator,
    result: op.result
  }));
  
  // Combine all actions in order: abandon, cell actions, operations, submit
  const allActions = [
    ...(abandonAction ? [abandonAction] : []),
    ...baseActions,
    ...operationActions,
    ...(submitAction ? [submitAction] : [])
  ];

  // Handle clicking outside color picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColorPicker]);

  // Get current theme colors
  const getCurrentThemeColors = () => {
    if (!allowedColors || allowedColors.length === 0) {
      return [];
    }

    const themePalettes = getThemePalettes();
    const currentTheme = userData?.theme;
    
    if (!themePalettes || !currentTheme) {
      return [];
    }
    
    const themeColors = themePalettes[currentTheme];
    if (!themeColors) {
      return [];
    }
    
    const mappedColors = allowedColors
      .filter(paletteKey => typeof paletteKey === 'string' && paletteKey.startsWith('palette.'))
      .map((paletteKey) => {
        const color = themeColors[paletteKey];
        if (!color) {
          return null;
        }
        return {
          key: paletteKey,
          color: color
        };
      })
      .filter(Boolean);
    
    return mappedColors;
  };

  // Handle color selection
  const handleColorSelection = (colorData) => {
    const paletteKey = colorData.key;
    colorCell(paletteKey);
    setShowColorPicker(false);
  };

  // Helper to get icon for action
  const getActionIcon = (action) => {
    switch (action) {
      case 'action_hint': return <Lightbulb size={18} />;
      case 'action_undo': return <Undo size={18} />;
      case 'action_erase': return <Eraser size={18} />;
      case 'action_abandon': return <LogOut size={18} />;
      case 'action_submit': return <Check size={18} />;
      case 'action_validate': return <Check size={18} />;
      case 'action_reveal': return <Eye size={18} />;
      case 'action_operation': return <Settings size={18} />;
      case 'action_delete': return <Trash2 size={18} />;
      case 'action_color': return <Palette size={18} />;
      case 'action_select': return <MousePointer size={18} />;
      case 'action_revert': return <SkipBack size={18} />;
      default:
        if (action.startsWith('operation_')) {
          return null; // We'll handle this in the render function
        }
        return null;
    }
  };

  const handleAction = (action) => {
    switch (action.action) {
      case 'action_hint':
        getHint().catch(() => {});
        break;
      case 'action_undo':
        undoMove();
        break;
      case 'action_erase':
        if (selectedCell) {
          eraseCell(selectedCell.x, selectedCell.y);
        }
        break;
      case 'action_abandon':
        setShowAbandonConfirmation(true);
        break;
      case 'action_submit':
        submitSolution();
        break;
      case 'action_validate':
        validatePuzzle();
        break;
      case 'action_reveal':
        if (selectedCell) {
          revealCell();
        }
        break;
      case 'action_revert':
        createRevert();
        break;
      case 'action_operation':
        console.log('Operation action - needs operation selector');
        break;
      case 'action_delete':
        if (selectedCell) {
          deleteCell();
        }
        break;
      case 'action_color':
        setShowColorPicker(!showColorPicker);
        break;
      case 'action_select':
        if (selectedCell) {
          selectCell(selectedCell.x, selectedCell.y);
        }
        break;
      default:
        if (action.action.startsWith('operation_')) {
          const operation = {
            operator: action.operator,
            result: action.result
          };
          operationCell(operation);
        } else {
          console.warn('Unknown action:', action.action);
        }
    }
  };

  // Helper to determine if an action should be enabled
  const isActionEnabled = (action) => {
    if (action.action === 'action_color') {
      try {
        const colors = getCurrentThemeColors();
        return colors.length > 0 && action.enabled;
      } catch (error) {
        return false;
      }
    }
    
    if (action.enabled) {
      return true;
    }
    
    if (action.toggle_on_select && selectedCell) {
      return true;
    }
    
    return false;
  };

  // Render a single action button (icon only)
  const renderActionButton = (action) => {
    const isOperationButton = action.action.startsWith('operation_');
    const isColorButton = action.action === 'action_color';
    const actionEnabled = isActionEnabled(action);
    
    return (
      <div className={styles.actionButtonWrapper} key={action.action}>
        <button
          type="button"
          disabled={loading || !actionEnabled}
          className={`${styles.actionButton} ${isOperationButton ? styles.operationButton : ''} ${isColorButton && showColorPicker ? styles.activeColorButton : ''}`}
          aria-label={isOperationButton ? `${action.result}${action.operator}` : getText(`actions.${action.action}`)}
          onClick={() => handleAction(action)}
          title={isOperationButton ? `${action.result}${action.operator}` : getText(`actions.${action.action}`)}
        >
          {isOperationButton ? (
            <div className={styles.operationContent}>
              {action.operator}
            </div>
          ) : (
            getActionIcon(action.action)
          )}
        </button>
        {isColorButton && showColorPicker && (
          <div ref={colorPickerRef} className={styles.colorPicker}>
            <div className={styles.colorGrid}>
              {getCurrentThemeColors().map((colorData) => (
                <button
                  key={colorData.key}
                  type="button"
                  className={styles.colorOption}
                  style={{ backgroundColor: colorData.color }}
                  onClick={() => handleColorSelection(colorData)}
                  aria-label={`Color ${colorData.key}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle abandon confirmation
  const handleAbandonConfirm = () => {
    abandonPuzzle();
    setShowAbandonConfirmation(false);
  };

  const handleAbandonCancel = () => {
    setShowAbandonConfirmation(false);
  };

  return (
    <>
      <div className={styles.actionPanel}>
        <div className={styles.buttonRow}>
          {allActions.map(action => renderActionButton(action))}
        </div>
      </div>
      
      {/* Abandon confirmation dialog */}
      <ConfirmationDialog
        isOpen={showAbandonConfirmation}
        onConfirm={handleAbandonConfirm}
        onCancel={handleAbandonCancel}
        title={getText('actions.action_abandon')}
        message={getText('actions.abandon_confirmation')}
        confirmText={getText('common_text.yes')}
        cancelText={getText('common_text.no')}
      />
    </>
  );
};

export default ActionPanelNarrow;